import type { LoomParameterControl } from "./loomParameterControls";

export interface LoomPinnedControlSet {
  nodeId: string;
  controlIds: readonly string[];
}

export interface LoomDesignGoal {
  variableControlId: string;
  observableId: string;
  targetValue: number;
  tolerance: number;
  status: "blocked" | "ready";
  blockedReason?: string;
}

export interface LoomControlWorkspace {
  schema: "loom-control-workspace/v1";
  pinnedControlsByNode: readonly LoomPinnedControlSet[];
  goal: LoomDesignGoal | null;
}

export type LoomControlWorkspaceIntent =
  | { kind: "controls.pin"; expectedRevision: number; controlIds: readonly string[] }
  | {
      kind: "goal.define";
      expectedRevision: number;
      variableControlId: string;
      observableId: string;
      targetValue: number;
      tolerance: number;
    };

export interface LoomObservableDescriptor {
  id: string;
  label: string;
  valueType: "number";
  unit?: string;
}

export type LoomGoalSolverCapability =
  | { canSolveGoals: true }
  | { canSolveGoals: false; reason: string };

export const LOOM_OBSERVABLES: readonly LoomObservableDescriptor[] = [
  { id: "aero.cl", label: "CL", valueType: "number", unit: "coefficient" },
];

export const LOOM_GOAL_CAPABILITY: LoomGoalSolverCapability = {
  canSolveGoals: false,
  reason: "Panel goal search is unavailable until each candidate revision rebuilds the hybrid mesh and passes trailing-edge and mesh-convergence validation.",
};

export interface LoomControlWorkspaceContext {
  selectedNodeId: string | null;
  controls: readonly LoomParameterControl[];
  observables: readonly LoomObservableDescriptor[];
  solverCapability: LoomGoalSolverCapability;
}

export interface LoomControlWorkspaceReduction {
  workspace: LoomControlWorkspace;
  geometryChange: "none";
}

type UnknownRecord = Record<string, unknown>;

function record(value: unknown, path: string): UnknownRecord {
  if (typeof value !== "object" || value === null || Array.isArray(value)) {
    throw new Error(`${path} must be an object`);
  }
  return value as UnknownRecord;
}

function exactKeys(value: UnknownRecord, allowed: readonly string[], path: string): void {
  const unknown = Object.keys(value).filter((key) => !allowed.includes(key));
  if (unknown.length > 0) throw new Error(`${path} contains unknown fields: ${unknown.join(", ")}`);
}

function nonEmptyString(value: unknown, path: string): string {
  if (typeof value !== "string" || value.trim() === "") throw new Error(`${path} must be a non-empty string`);
  return value;
}

function finiteNumber(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isFinite(value)) throw new Error(`${path} must be a finite number`);
  return value;
}

function nonnegativeInteger(value: unknown, path: string): number {
  if (typeof value !== "number" || !Number.isSafeInteger(value) || value < 0) {
    throw new Error(`${path} must be a nonnegative safe integer`);
  }
  return value;
}

function uniqueStrings(value: unknown, path: string): string[] {
  if (!Array.isArray(value)) throw new Error(`${path} must be an array`);
  const result = value.map((item, index) => nonEmptyString(item, `${path}[${index}]`));
  if (new Set(result).size !== result.length) throw new Error(`${path} contains duplicate ids`);
  return result;
}

function parseGoal(value: unknown, path: string): LoomDesignGoal {
  const item = record(value, path);
  exactKeys(item, ["variableControlId", "observableId", "targetValue", "tolerance", "status", "blockedReason"], path);
  const status = item.status;
  if (status !== "blocked" && status !== "ready") throw new Error(`${path}.status is unsupported`);
  const tolerance = finiteNumber(item.tolerance, `${path}.tolerance`);
  if (tolerance <= 0) throw new Error(`${path}.tolerance must be positive`);
  const blockedReason = item.blockedReason === undefined
    ? undefined
    : nonEmptyString(item.blockedReason, `${path}.blockedReason`);
  if (status === "ready" && blockedReason !== undefined) throw new Error(`${path}.blockedReason is only valid when blocked`);
  return {
    variableControlId: nonEmptyString(item.variableControlId, `${path}.variableControlId`),
    observableId: nonEmptyString(item.observableId, `${path}.observableId`),
    targetValue: finiteNumber(item.targetValue, `${path}.targetValue`),
    tolerance,
    status,
    ...(blockedReason === undefined ? {} : { blockedReason }),
  };
}

export function parseLoomControlWorkspace(value: unknown): LoomControlWorkspace {
  const workspace = record(value, "workspace");
  exactKeys(workspace, ["schema", "pinnedControlsByNode", "goal"], "workspace");
  if (workspace.schema !== "loom-control-workspace/v1") {
    throw new Error("workspace.schema must be loom-control-workspace/v1");
  }
  if (!Array.isArray(workspace.pinnedControlsByNode)) {
    throw new Error("workspace.pinnedControlsByNode must be an array");
  }
  const pinnedControlsByNode = workspace.pinnedControlsByNode.map((raw, index): LoomPinnedControlSet => {
    const item = record(raw, `workspace.pinnedControlsByNode[${index}]`);
    exactKeys(item, ["nodeId", "controlIds"], `workspace.pinnedControlsByNode[${index}]`);
    return {
      nodeId: nonEmptyString(item.nodeId, `workspace.pinnedControlsByNode[${index}].nodeId`),
      controlIds: uniqueStrings(item.controlIds, `workspace.pinnedControlsByNode[${index}].controlIds`),
    };
  });
  if (new Set(pinnedControlsByNode.map(({ nodeId }) => nodeId)).size !== pinnedControlsByNode.length) {
    throw new Error("workspace.pinnedControlsByNode contains duplicate node ids");
  }
  return {
    schema: "loom-control-workspace/v1",
    pinnedControlsByNode,
    goal: workspace.goal === null ? null : parseGoal(workspace.goal, "workspace.goal"),
  };
}

export function parseLoomControlWorkspaceIntent(value: unknown): LoomControlWorkspaceIntent {
  const intent = record(value, "intent");
  if (intent.kind === "controls.pin") {
    exactKeys(intent, ["kind", "expectedRevision", "controlIds"], "intent");
    return {
      kind: "controls.pin",
      expectedRevision: nonnegativeInteger(intent.expectedRevision, "intent.expectedRevision"),
      controlIds: uniqueStrings(intent.controlIds, "intent.controlIds"),
    };
  }
  if (intent.kind === "goal.define") {
    exactKeys(intent, ["kind", "expectedRevision", "variableControlId", "observableId", "targetValue", "tolerance"], "intent");
    const tolerance = finiteNumber(intent.tolerance, "intent.tolerance");
    if (tolerance <= 0) throw new Error("intent.tolerance must be positive");
    return {
      kind: "goal.define",
      expectedRevision: nonnegativeInteger(intent.expectedRevision, "intent.expectedRevision"),
      variableControlId: nonEmptyString(intent.variableControlId, "intent.variableControlId"),
      observableId: nonEmptyString(intent.observableId, "intent.observableId"),
      targetValue: finiteNumber(intent.targetValue, "intent.targetValue"),
      tolerance,
    };
  }
  throw new Error("intent.kind is unsupported");
}

export function createEmptyLoomControlWorkspace(): LoomControlWorkspace {
  return { schema: "loom-control-workspace/v1", pinnedControlsByNode: [], goal: null };
}

export function pinnedLoomControlIds(workspaceValue: unknown, nodeId: string): readonly string[] {
  const workspace = parseLoomControlWorkspace(workspaceValue);
  return workspace.pinnedControlsByNode.find((entry) => entry.nodeId === nodeId)?.controlIds ?? [];
}

function controlCatalog(controls: readonly LoomParameterControl[]): Map<string, LoomParameterControl> {
  const byId = new Map(controls.map((control) => [control.id, control]));
  if (byId.size !== controls.length) throw new Error("control catalog contains duplicate ids");
  return byId;
}

function observableCatalog(observables: readonly LoomObservableDescriptor[]): Map<string, LoomObservableDescriptor> {
  const byId = new Map<string, LoomObservableDescriptor>();
  observables.forEach((observable, index) => {
    nonEmptyString(observable.id, `observables[${index}].id`);
    nonEmptyString(observable.label, `observables[${index}].label`);
    if (observable.valueType !== "number") throw new Error(`observables[${index}].valueType must be number`);
    if (byId.has(observable.id)) throw new Error("observable catalog contains duplicate ids");
    byId.set(observable.id, observable);
  });
  return byId;
}

function validateSolverCapability(capability: LoomGoalSolverCapability): void {
  if (capability.canSolveGoals === true) return;
  if (capability.canSolveGoals !== false || typeof capability.reason !== "string" || capability.reason.trim() === "") {
    throw new Error("blocked solver capability requires a reason");
  }
}

export function reduceLoomControlWorkspace(
  workspaceValue: unknown,
  intentValue: unknown,
  context: LoomControlWorkspaceContext,
): LoomControlWorkspaceReduction {
  const workspace = parseLoomControlWorkspace(workspaceValue);
  const intent = parseLoomControlWorkspaceIntent(intentValue);
  const controls = controlCatalog(context.controls);

  if (intent.kind === "controls.pin") {
    const nodeId = context.selectedNodeId;
    if (typeof nodeId !== "string" || nodeId.trim() === "") throw new Error("controls.pin requires a selected node");
    intent.controlIds.forEach((controlId) => {
      const control = controls.get(controlId);
      if (!control) throw new Error(`unknown Loom control ${controlId}`);
      if (control.locator.nodeId !== nodeId) throw new Error(`control ${controlId} does not belong to selected node ${nodeId}`);
    });
    const retained = workspace.pinnedControlsByNode.filter((entry) => entry.nodeId !== nodeId);
    const pinnedControlsByNode = intent.controlIds.length === 0
      ? retained
      : [...retained, { nodeId, controlIds: [...intent.controlIds].sort() }];
    pinnedControlsByNode.sort((left, right) => left.nodeId.localeCompare(right.nodeId));
    return {
      workspace: { ...workspace, pinnedControlsByNode },
      geometryChange: "none",
    };
  }

  const variable = controls.get(intent.variableControlId);
  if (!variable) throw new Error(`unknown Loom control ${intent.variableControlId}`);
  if (variable.editor.kind !== "range" || typeof variable.value !== "number") {
    throw new Error(`goal variable ${intent.variableControlId} must be a numeric range control`);
  }
  if (!observableCatalog(context.observables).has(intent.observableId)) {
    throw new Error(`unknown Loom observable ${intent.observableId}`);
  }
  validateSolverCapability(context.solverCapability);
  const available = context.solverCapability.canSolveGoals;
  return {
    workspace: {
      ...workspace,
      goal: {
        variableControlId: intent.variableControlId,
        observableId: intent.observableId,
        targetValue: intent.targetValue,
        tolerance: intent.tolerance,
        status: available ? "ready" : "blocked",
        ...(available ? {} : { blockedReason: context.solverCapability.reason }),
      },
    },
    geometryChange: "none",
  };
}
